import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Tabs, 
  Tab, 
  Divider, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  DialogContentText,
  Tooltip,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Print as PrintIcon,
  CheckCircle as ReceivedIcon,
  Schedule as PendingIcon,
  ArrowBack as BackIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  FlashOn as QuickActionsIcon
} from '@mui/icons-material';
import { useJob } from '../contexts/JobContext';
import { useInventory } from '../contexts/InventoryContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { jobApi, inventoryApi, planningApi } from '../services/api.service';
import BarcodeScanner from '../components/BarcodeScanner';
import StatusHistoryDialog from '../components/StatusHistoryDialog';
import JobSiteManager from '../components/JobSiteManager';
import JobEquipmentPlanning from '../components/JobEquipmentPlanning';
import QuickActionsModal from '../components/QuickActionsModal';
import JobLocationQRPoster from '../components/JobLocationQRPoster';
import { useSnackbar } from 'notistack';
import LinkifiedText from '../components/LinkifiedText';
import JobProjectView from '../components/ProjectManagement/JobProjectView';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { jobList, loading: jobsLoading, error: jobsError, updateJob, updateJobEquipment, deleteJobEquipment } = useJob();
  const { isFeatureEnabled } = useFeatureFlags();
  const { loading: inventoryLoading, error: inventoryError } = useInventory();
  const { warehouses, racks, bays, getWarehouseName, getRackName, getBayName } = useInventoryPage();
  const { enqueueSnackbar } = useSnackbar();
  const hasPMAccess = useFeatureFlag('pm_demo');
  
  // State for job data
  const [job, setJob] = useState(null);
  const [jobEquipment, setJobEquipment] = useState([]);
  const [equipmentPlans, setEquipmentPlans] = useState([]);
  const [expandedSites, setExpandedSites] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed tabs - now using single consolidated view
  
  // Notes editing state
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');

  // Quick Actions modal state
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Job Location QR Poster state
  const [showJobQRPoster, setShowJobQRPoster] = useState(false);

  // Note: warehouses and racks now come from InventoryPageContext

  // Equipment dialog state
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [equipmentFormData, setEquipmentFormData] = useState({
    description: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    partNumber: '',
    quantity: 1,
    siteId: '',
    isReceived: false,
    receivedDate: '',
    warehouseLocation: '',
    notes: ''
  });
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [equipmentToEdit, setEquipmentToEdit] = useState(null);
  
  // Site management is now handled by JobSiteManager component
  // Equipment history dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  // Multi-stack location state
  const [editingStackLocations, setEditingStackLocations] = useState(false);
  const [tempStackLocations, setTempStackLocations] = useState([]);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Removed old dialog state
  const [siteFormData, setSiteFormData] = useState({
    name: '',
    address: '',
    contactName: '',
    contactPhone: '',
    notes: ''
  });
  const [siteFormMode, setSiteFormMode] = useState('add'); // 'add' or 'edit'
  const [siteToEdit, setSiteToEdit] = useState(null);

  // Load job data
  useEffect(() => {
    const loadJobData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Special handling for PM demo job
        if (jobId === 'pm-demo-job' && hasPMAccess) {
          const pmDemoJob = {
            id: 'pm-demo-job',
            jobNumber: 'J2024-087',
            name: 'Project Management Demo - Noblesville High School',
            client: 'Noblesville Schools',
            location: 'Noblesville, IN',
            status: 'Active',
            startDate: '2024-09-15',
            endDate: '2024-10-30',
            notes: 'This is a demo job showcasing integrated project management features with equipment tracking.',
            sites: [
              {
                id: 'pm-site-1',
                name: 'Main Campus',
                address: '300 N 16th St',
                city: 'Noblesville',
                state: 'IN',
                equipment: []
              }
            ],
            // PM-specific fields for demo
            budgetHours: 80,
            actualHours: 52,
            hasLift: true,
            liftHeight: '35 ft',
            pmName: 'Matt',
            pmColor: '#2196f3'
          };

          setJob(pmDemoJob);
          setNotesText(pmDemoJob.notes || '');

          // Set mock equipment plans for demo
          setEquipmentPlans([
            { id: 'plan-1', needed_quantity: 10, description: 'Dome Cameras' },
            { id: 'plan-2', needed_quantity: 2, description: 'PTZ Cameras' },
            { id: 'plan-3', needed_quantity: 1, description: 'Network Video Recorder' },
            { id: 'plan-4', needed_quantity: 20, description: 'Camera Mounts' },
            { id: 'plan-5', needed_quantity: 12, description: 'Cable Runs' }
          ]);

          // Set mock equipment for demo
          setJobEquipment([
            { id: 'eq-1', status: 'installed', isReceived: true, description: 'Dome Camera - Main Entry' },
            { id: 'eq-2', status: 'installed', isReceived: true, description: 'Dome Camera - Cafeteria' },
            { id: 'eq-3', status: 'received', isReceived: true, description: 'Dome Camera - Hallway A' },
            { id: 'eq-4', status: 'received', isReceived: true, description: 'Dome Camera - Hallway B' },
            { id: 'eq-5', status: 'received', isReceived: true, description: 'Cat6 Cable (1000ft)' },
            { id: 'eq-6', status: 'received', isReceived: true, description: 'Camera Mounts (Box of 20)' },
          ]);

          setLoading(false);
          return;
        }

        // Find job in job list first
        let jobData = jobList.find(j => j.id === jobId);

        // If not found, fetch from API
        if (!jobData) {
          const response = await jobApi.getJobById(jobId);
          jobData = response.data;
        }

        setJob(jobData);
        setNotesText(jobData.notes || '');

        // Load job equipment
        await loadJobEquipment(jobData);

        // Warehouses are loaded by InventoryPageContext
      } catch (err) {
        console.error('Error loading job details:', err);
        setError('Failed to load job details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      loadJobData();
    }
  }, [jobId, jobList, hasPMAccess]);

  // Load job equipment data
  const loadJobEquipment = async (jobData) => {
    try {
      console.log('Loading equipment for job:', jobData.id);
      
      // Get actual assigned equipment items
      const equipmentResponse = await jobApi.getJobEquipment(jobData.id);
      const equipmentData = equipmentResponse.data?.equipment || equipmentResponse.data || [];
      console.log(`[JOB EQUIPMENT DEBUG] Loaded ${equipmentData.length} equipment items for job ${jobData.id}:`, equipmentData);
      
      // Also get planning data for progress calculation
      let plannedEquipment = [];
      try {
        const planningResponse = await planningApi.getJobEquipmentPlans(jobData.id);
        plannedEquipment = planningResponse.data?.data || planningResponse.data || [];
        // Update planning state for job stats calculation
        setEquipmentPlans(plannedEquipment);
      } catch (planningError) {
        console.warn('Could not load planning data:', planningError);
        setEquipmentPlans([]);
      }
      
      console.log('Received equipment response:', equipmentResponse.data);
      console.log('Received planning response:', plannedEquipment);
      console.log('Extracted equipment data:', equipmentData);
      console.log('Equipment data is array:', Array.isArray(equipmentData));
      
      // Ensure we always set an array
      const safeEquipmentData = Array.isArray(equipmentData) ? equipmentData : [];
      
      // Initialize expanded sites state - auto-expand sites with equipment
      if (jobData.sites && jobData.sites.length > 0) {
        const expandedState = {};
        jobData.sites.forEach(site => {
          // Auto-expand if this site has equipment assigned
          const siteHasEquipment = safeEquipmentData.some(item => item.siteId === site.id.toString());
          expandedState[site.id] = siteHasEquipment;
        });
        setExpandedSites(expandedState);
      }
      setJobEquipment(safeEquipmentData);
    } catch (err) {
      console.error('Error loading job equipment:', err);
      
      // In development mode, use the same equipment service as JobEquipmentTab
      if (import.meta.env.DEV && jobData && jobData.sites) {
        try {
          console.log('DEV MODE: Using equipment service for consistent data');
          
          // Import equipment service dynamically
          const equipmentService = await import('../services/equipment.service');
          
          // Get equipment data using the same method as JobEquipmentTab
          const result = await equipmentService.default.getJobEquipment(jobData.id, {
            includeReturned: true
          });
          
          // Use the assignments array from the result
          const mockEquipment = result.assignments || [];
          
          console.log('Created mock equipment via equipment service:', mockEquipment);
          setJobEquipment(mockEquipment);
          return;
        } catch (serviceError) {
          console.error('Error using equipment service:', serviceError);
          
          // Fall back to original method if service fails
          console.log('Falling back to direct site equipment from job data');
          
          // Create mock equipment from the site equipment arrays
          const mockEquipment = [];
          
          // Generate mock equipment based on site.equipment arrays
          jobData.sites.forEach(site => {
            if (site.equipment && Array.isArray(site.equipment)) {
              console.log(`Site ${site.id} has ${site.equipment.length} equipment items`);
              
              site.equipment.forEach((equipItem, idx) => {
                // If it's a string, it's just an item code
                if (typeof equipItem === 'string') {
                  mockEquipment.push({
                    id: `${site.id}-${equipItem}-${idx}`,
                    siteId: site.id,
                    equipmentId: equipItem,
                    description: `Equipment ${equipItem}`,
                    isReceived: Math.random() > 0.5, // Randomly set for testing
                    receivedDate: new Date().toISOString().split('T')[0],
                    quantity: 1
                  });
                } 
                // If it's an object, it already has details
                else if (typeof equipItem === 'object') {
                  mockEquipment.push({
                    id: equipItem.id || `${site.id}-${equipItem.id}-${idx}`,
                    siteId: site.id,
                    equipmentId: equipItem.id,
                    description: equipItem.description,
                    isReceived: equipItem.isReceived !== undefined ? equipItem.isReceived : (Math.random() > 0.5),
                    receivedDate: equipItem.receivedDate || new Date().toISOString().split('T')[0],
                    quantity: equipItem.quantity || 1
                  });
                }
              });
            }
          });
          
          console.log('Created mock equipment (fallback):', mockEquipment);
          setJobEquipment(mockEquipment);
          return;
        }
      }
      
      throw err;
    }
  };
  
  // Bulk update equipment status
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState({
    isReceived: true,
    receivedDate: new Date().toISOString().split('T')[0],
    warehouseLocation: ''
  });
  
  // Open bulk update dialog
  const handleOpenBulkUpdate = (siteId = '') => {
    setSelectedSiteId(siteId);
    setBulkUpdateStatus({
      isReceived: true,
      receivedDate: new Date().toISOString().split('T')[0],
      warehouseLocation: ''
    });
    setShowBulkUpdateDialog(true);
  };
  
  // Handle bulk update status change
  const handleBulkStatusChange = (e) => {
    const { name, value, checked } = e.target;
    
    // For checkbox (isReceived)
    if (name === 'isReceived') {
      setBulkUpdateStatus(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setBulkUpdateStatus(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Apply bulk update
  const handleApplyBulkUpdate = async () => {
    setLoading(true);
    try {
      // Get equipment items to update
      let itemsToUpdate = [];
      
      if (selectedSiteId) {
        // Update items for a specific site
        itemsToUpdate = jobEquipment.filter(item => 
          item.siteId === selectedSiteId && !item.isReceived
        );
      } else {
        // Update all pending items across all sites
        itemsToUpdate = jobEquipment.filter(item => !item.isReceived);
      }
      
      // No items to update
      if (itemsToUpdate.length === 0) {
        setError('No pending equipment items found to update.');
        setShowBulkUpdateDialog(false);
        setLoading(false);
        return;
      }
      
      // Update each item
      const updatePromises = itemsToUpdate.map(item => {
        const updatedItem = {
          ...item,
          isReceived: bulkUpdateStatus.isReceived,
          receivedDate: bulkUpdateStatus.receivedDate,
          warehouseLocation: bulkUpdateStatus.warehouseLocation
        };
        
        return updateJobEquipment(job.id, item.siteId, updatedItem);
      });
      
      // Wait for all updates to complete
      const updatedItems = await Promise.all(updatePromises);
      
      // Update local state
      setJobEquipment(prev => {
        const updated = [...prev];
        updatedItems.forEach(updatedItem => {
          const index = updated.findIndex(item => item.id === updatedItem.id);
          if (index !== -1) {
            updated[index] = updatedItem;
          }
        });
        return updated;
      });
      
      // Close dialog
      setShowBulkUpdateDialog(false);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error in bulk update:', err);
      setError('Failed to update equipment status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle site expansion
  const toggleSiteExpanded = (siteId) => {
    setExpandedSites(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  // Handle equipment history
  const handleShowHistory = (item) => {
    setHistoryItem(item);
    setHistoryDialogOpen(true);
  };

  const handleHistoryClose = () => {
    setHistoryDialogOpen(false);
    setHistoryItem(null);
  };

  // Handle tab change
  // Removed tab handling - using single view

  // Note: Warehouses are now loaded by InventoryPageContext
  // Removed loadWarehouses function

  // Handle updating stack location
  const handleUpdateStackLocation = async (field, value) => {
    console.log(`Updating stack location: ${field} = ${value}`);
    try {
      // Clear dependent fields when parent changes
      // Convert empty strings to null for proper validation
      const cleanValue = value === '' ? null : value;
      let updateData = { [field]: cleanValue };
      
      // Note: Racks are now independent of warehouses
      // No need to clear dependent fields
      
      // Update job locally first for immediate UI update
      setJob(prev => ({ ...prev, ...updateData }));
      
      // Update in database directly (avoid context loading state)
      console.log(`[STACK LOCATION DEBUG] Sending update to API:`, updateData);
      console.log(`[STACK LOCATION DEBUG] Job ID:`, jobId);
      console.log(`[STACK LOCATION DEBUG] Field being updated:`, field);
      console.log(`[STACK LOCATION DEBUG] Value being set:`, value);
      
      const response = await jobApi.updateJob(jobId, updateData);
      const updatedJob = response.data;
      console.log(`[STACK LOCATION DEBUG] API response:`, updatedJob);
      console.log(`[STACK LOCATION DEBUG] Response type:`, typeof updatedJob);
      console.log(`[STACK LOCATION DEBUG] Response success:`, response.status === 200);
      
      // Only update local state if the response is valid
      if (updatedJob && updatedJob.id === jobId) {
        setJob(updatedJob);
        console.log(`Job updated successfully - new ${field}:`, updatedJob[field]);
        console.log('Full updated job:', updatedJob);
      } else {
        console.error('Updated job data invalid:', { updatedJob, jobId });
      }
      
      // Show success notification
      if (field === 'stackWarehouseId') {
        const warehouse = warehouses.find(w => w.id === value);
        enqueueSnackbar(`Stack location updated to ${warehouse?.name || 'None'}`, { variant: 'success' });
      } else if (field === 'stackRackId') {
        const rack = racks.find(r => r.id === value);
        enqueueSnackbar(`Rack updated to ${rack?.name || 'None'}`, { variant: 'success' });
      } else if (field === 'stackBayId') {
        const bay = bays.find(b => b.id === value);
        enqueueSnackbar(`Bay updated to ${bay?.name || 'None'}`, { variant: 'success' });
      }
      
    } catch (error) {
      console.error('[STACK LOCATION ERROR] Failed to update stack location:', error);
      console.error('[STACK LOCATION ERROR] Error details:', error.response?.data || error.message);
      console.error('[STACK LOCATION ERROR] Error status:', error.response?.status);
      console.error('[STACK LOCATION ERROR] Full error response:', error.response);
      console.error('[STACK LOCATION ERROR] Field that failed:', field);
      console.error('[STACK LOCATION ERROR] Value that failed:', value);
      setError('Failed to update stack location. Please try again.');
      
      // Only revert the specific field that failed, not the entire job state
      // This prevents warehouse selection from being lost when rack selection fails
      if (field === 'stackWarehouseId') {
        // If warehouse update failed, revert warehouse selection
        setJob(prev => ({ ...prev, stackWarehouseId: job.stackWarehouseId }));
        // Note: InventoryPageContext will handle rack loading based on warehouse selection
      } else if (field === 'stackRackId') {
        // If rack update failed, only revert the rack selection
        setJob(prev => ({ ...prev, stackRackId: job.stackRackId }));
      }
    }
  };

  // Handle saving notes
  const handleSaveNotes = async () => {
    try {
      await updateJob(jobId, { notes: notesText });
      setJob(prev => ({ ...prev, notes: notesText }));
      setEditingNotes(false);
      // You could add a success snackbar here
    } catch (error) {
      console.error('Failed to save notes:', error);
      setError('Failed to save notes. Please try again.');
    }
  };

  // Handle opening equipment dialog
  const handleOpenEquipmentDialog = (mode, equipment = null) => {
    if (mode === 'edit' && equipment) {
      setEquipmentFormData({
        description: equipment.description || '',
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serialNumber: equipment.serialNumber || '',
        partNumber: equipment.partNumber || '',
        quantity: equipment.quantity || 1,
        siteId: equipment.siteId || '',
        isReceived: equipment.isReceived || false,
        receivedDate: equipment.receivedDate || '',
        warehouseLocation: equipment.warehouseLocation || '',
        notes: equipment.notes || ''
      });
      setEquipmentToEdit(equipment);
    } else {
      // Default to first site if available
      const defaultSiteId = job?.sites && job.sites.length > 0 ? job.sites[0].id : '';
      
      setEquipmentFormData({
        description: '',
        manufacturer: '',
        model: '',
        serialNumber: '',
        partNumber: '',
        quantity: 1,
        siteId: defaultSiteId,
        isReceived: false,
        receivedDate: '',
        warehouseLocation: '',
        notes: ''
      });
      setEquipmentToEdit(null);
    }
    
    setFormMode(mode);
    setShowEquipmentDialog(true);
  };

  // Handle equipment form changes
  const handleEquipmentFormChange = (e) => {
    const { name, value, checked } = e.target;
    
    // For checkbox (isReceived)
    if (name === 'isReceived') {
      setEquipmentFormData(prev => ({
        ...prev,
        [name]: checked,
        // If marking as received, set today's date as default if empty
        receivedDate: checked && !prev.receivedDate ? new Date().toISOString().split('T')[0] : prev.receivedDate
      }));
    } else {
      setEquipmentFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle equipment save
  const handleSaveEquipment = async () => {
    // Validate form
    if (!equipmentFormData.description || !equipmentFormData.siteId) {
      setError('Please fill all required fields: Description and Site');
      return;
    }
    
    setLoading(true);
    try {
      // Create new equipment object with auto-generated ID if needed
      const equipmentToSave = {
        id: equipmentToEdit ? equipmentToEdit.id : `EQ${Date.now().toString().substr(-6)}`,
        ...equipmentFormData,
        siteName: job.sites.find(site => site.id === equipmentFormData.siteId)?.name || ''
      };
      
      // Use JobContext method instead of direct API call to keep everything in sync
      const savedEquipment = await updateJobEquipment(
        job.id,
        equipmentFormData.siteId,
        equipmentToSave
      );
      
      if (formMode === 'edit') {
        // Update existing equipment
        setJobEquipment(prev => prev.map(item => 
          item.id === savedEquipment.id ? savedEquipment : item
        ));
      } else {
        // Add new equipment
        setJobEquipment(prev => [...prev, savedEquipment]);
      }
      
      // Close dialog
      setShowEquipmentDialog(false);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error saving equipment:', err);
      // More specific error messages
      if (err.message && err.message.includes('not found')) {
        setError(err.message);
      } else {
        setError('Failed to save equipment. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);
  
  // Open confirm dialog
  const openDeleteConfirmation = (equipment) => {
    setEquipmentToDelete(equipment);
    setConfirmDialogOpen(true);
  };
  
  // Close confirm dialog
  const closeDeleteConfirmation = () => {
    setEquipmentToDelete(null);
    setConfirmDialogOpen(false);
  };
  
  // Handle equipment delete
  const handleDeleteEquipment = async (equipmentId) => {
    setLoading(true);
    try {
      // Find the item to get its siteId
      const itemToDelete = jobEquipment.find(item => item.id === equipmentId);
      if (!itemToDelete) {
        setError('Equipment not found. It may have been deleted already.');
        return;
      }
      
      // Use context method instead of direct API call to keep everything in sync
      await deleteJobEquipment(job.id, itemToDelete.siteId, equipmentId);
      
      // Remove from state
      setJobEquipment(prev => prev.filter(item => item.id !== equipmentId));
      
      // Success message
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error deleting equipment:', err);
      // More specific error messages
      if (err.message && err.message.includes('not found')) {
        setError(err.message);
      } else {
        setError('Failed to delete equipment. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
      closeDeleteConfirmation();
    }
  };

  // Calculate job statistics
  const calculateJobStats = () => {
    // For job stats, we need to use PLANNING data (what's planned) not assignment data (what's assigned)
    // This matches the equipment planning component which shows the correct numbers
    
    console.log('calculateJobStats - equipmentPlans:', equipmentPlans);
    console.log('calculateJobStats - jobEquipment:', jobEquipment);
    console.log('calculateJobStats - equipmentPlans length:', equipmentPlans?.length);
    console.log('calculateJobStats - individual needed_quantity values:', equipmentPlans?.map(p => ({ id: p.id, needed_quantity: p.needed_quantity, description: p.description })));
    
    if (!equipmentPlans || equipmentPlans.length === 0) {
      return {
        totalItems: 0,
        receivedItems: 0,
        pendingItems: 0,
        receivedPercentage: 0
      };
    }
    
    // Calculate totals from planning data
    // Use needed_quantity field which is what's in the actual plan data
    const totalItems = equipmentPlans.reduce((sum, plan) => sum + (plan.needed_quantity || 0), 0);
    
    // For received items, we need to count equipment that has been assigned to this job
    // The jobEquipment array contains actual equipment assignments
    // Count items that have been received and designated for this job/site
    // This includes items that are assigned, checked out to technicians, or installed
    const receivedItems = jobEquipment.filter(item => {
      // Check multiple ways equipment might be marked as received/assigned
      return item.isReceived ||
             item.status === 'received' ||
             item.status === 'assigned' ||
             item.status === 'checked_out' ||
             item.status === 'installed';
    }).length;
    
    const pendingItems = Math.max(0, totalItems - receivedItems);
    const receivedPercentage = totalItems > 0 ? Math.round((receivedItems / totalItems) * 100) : 0;
    
    return {
      totalItems,
      receivedItems,
      pendingItems,
      receivedPercentage
    };
  };

  // Get equipment for a specific site
  const getSiteEquipment = (siteId) => {
    return jobEquipment.filter(item => item.siteId === siteId);
  };

  // Check if site has pending equipment
  const hasPendingEquipment = (siteId) => {
    const siteEquipment = getSiteEquipment(siteId);
    return siteEquipment.some(item => !item.isReceived);
  };

  // Go back to jobs list
  const handleBack = () => {
    navigate('/jobs');
  };

  // Handle stack location save
  const handleStackLocationSave = async () => {
    try {
      await updateJob(jobId, { stackLocations: tempStackLocations });
      setJob(prev => ({ ...prev, stackLocations: tempStackLocations }));
      setEditingStackLocations(false);
      enqueueSnackbar('Stack locations updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update stack locations', { variant: 'error' });
    }
  };

  // Handle notes update
  const handleNotesUpdate = async () => {
    try {
      await updateJob(jobId, { notes: notesText });
      setJob(prev => ({ ...prev, notes: notesText }));
      setEditingNotes(false);
      enqueueSnackbar('Notes updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update notes', { variant: 'error' });
    }
  };

  // Handle job deletion
  const handleDeleteJob = async () => {
    try {
      // Assuming there's a deleteJob method in the context or API
      await jobApi.deleteJob(jobId);
      enqueueSnackbar('Job deleted successfully', { variant: 'success' });
      navigate('/jobs');
    } catch (error) {
      enqueueSnackbar('Failed to delete job', { variant: 'error' });
    }
  };

  // Site management handlers
  
  // Open site dialog for adding a new site
  const handleAddSite = () => {
    setSiteFormMode('add');
    setSiteToEdit(null);
    setSiteFormData({
      name: '',
      address: '',
      contactName: '',
      contactPhone: '',
      notes: ''
    });
    setShowSiteDialog(true);
  };
  
  // Open site dialog for editing an existing site
  const handleEditSite = (site) => {
    setSiteFormMode('edit');
    setSiteToEdit(site);
    setSiteFormData({
      name: site.name || '',
      address: site.address || '',
      contactName: site.contactName || '',
      contactPhone: site.contactPhone || '',
      notes: site.notes || ''
    });
    setShowSiteDialog(true);
  };
  
  // Handle site form input changes
  const handleSiteFormChange = (e) => {
    const { name, value } = e.target;
    setSiteFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Submit site form (add or edit)
  const handleSiteSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (siteFormMode === 'add') {
        // Create new site
        const newSite = {
          ...siteFormData
        };
        
        // API call to create site
        const response = await jobApi.createJobSite(job.id, newSite);
        const createdSite = response.data;
        
        // Update job data
        setJob(prev => ({
          ...prev,
          sites: [...(prev.sites || []), createdSite]
        }));
        
        // Initialize expanded state for new site
        setExpandedSites(prev => ({
          ...prev,
          [createdSite.id]: false
        }));
        
      } else if (siteFormMode === 'edit' && siteToEdit) {
        // Update existing site
        const updatedSite = {
          ...siteToEdit,
          ...siteFormData
        };
        
        // API call to update site
        const response = await jobApi.updateJobSite(job.id, siteToEdit.id, siteFormData);
        const updatedSiteFromApi = response.data;
        
        // Update job data
        setJob(prev => ({
          ...prev,
          sites: prev.sites.map(site => 
            site.id === updatedSiteFromApi.id ? updatedSiteFromApi : site
          )
        }));
      }
      
      // Close dialog
      setShowSiteDialog(false);
      
    } catch (err) {
      console.error('Error managing site:', err);
      setError('Failed to save site. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a site
  const handleDeleteSite = async (siteId) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this site? All equipment assigned to this site will be removed.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // API call to delete site
      await jobApi.deleteJobSite(job.id, siteId);
      
      // Update job data
      setJob(prev => ({
        ...prev,
        sites: prev.sites.filter(site => site.id !== siteId)
      }));
      
      // Remove from expanded sites
      const newExpandedSites = { ...expandedSites };
      delete newExpandedSites[siteId];
      setExpandedSites(newExpandedSites);
      
      // Update job equipment list
      setJobEquipment(prev => prev.filter(item => item.siteId !== siteId));
      
    } catch (err) {
      console.error('Error deleting site:', err);
      setError('Failed to delete site. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Job stats
  const jobStats = calculateJobStats();

  // Loading state
  if (loading || jobsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || jobsError || !job) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">
          {error || jobsError || 'Job not found'}
        </Alert>
        <Button 
          startIcon={<BackIcon />} 
          onClick={handleBack} 
          sx={{ mt: 2 }}
        >
          Back to Jobs List
        </Button>
      </Box>
    );
  }

  const isPMDemo = hasPMAccess && (job?.id === 'pm-demo-job' || job?.jobNumber === 'J2024-087' || job?.name?.includes('Project Management Demo'));

  return (
    <Container maxWidth={isPMDemo ? false : "xl"} sx={{ py: 2 }}>
      {/* Header with job info - skip for PM demo jobs */}
      {!isPMDemo && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <BackIcon />
              </IconButton>
              <Box>
                <Typography variant={isMobile ? "h5" : "h4"} component="h1">
                  {job.name}
                </Typography>
                {(job?.stackLocations?.length > 0 || job?.stackRackId) && (
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.75rem' }}>
                    Stack Location: {(() => {
                      // Handle new multi-stack format
                      if (job?.stackLocations && job.stackLocations.length > 0) {
                        return job.stackLocations.map(location => {
                          const warehouse = warehouses.find(w => w.id === location.warehouseId);
                          const rack = racks.find(r => r.id === location.rackId);
                          const bay = bays.find(b => b.id === location.bayId);
                          if (warehouse && rack && bay) {
                            return `${warehouse.name}-${rack.name} ${bay.name}`;
                          } else if (warehouse && rack) {
                            return `${warehouse.name}-${rack.name}`;
                          } else if (rack && bay) {
                            return `${rack.name}${bay.name}`;
                          } else if (rack) {
                            return `${rack.name}`;
                          }
                          return 'Unknown';
                        }).join(', ');
                      }
                      // Handle legacy single-stack format
                      else if (job?.stackRackId) {
                        const warehouse = warehouses.find(w => w.id === job.stackWarehouseId);
                        const rack = racks.find(r => r.id === job.stackRackId);
                        const bay = bays.find(b => b.id === job.stackBayId);
                        if (warehouse && rack && bay) {
                          return `${warehouse.name}-${rack.name} ${bay.name}`;
                        } else if (warehouse && rack) {
                          return `${warehouse.name}-${rack.name}`;
                        } else if (rack && bay) {
                          return `${rack.name}${bay.name}`;
                        } else if (rack) {
                          return `${rack.name}`;
                        }
                        return 'Not Set';
                      }
                      return 'Not Set';
                    })()}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Quick Actions - Scan and process equipment">
                <Button
                  variant="contained"
                  startIcon={<QuickActionsIcon />}
                  onClick={() => setShowQuickActions(true)}
                  size="small"
                >
                  Quick Actions
                </Button>
              </Tooltip>
              <Tooltip title="Print job location QR poster">
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={() => setShowJobQRPoster(true)}
                  size="small"
                >
                  Print Job Page
                </Button>
              </Tooltip>
              <Tooltip title="Delete this job">
                <IconButton
                  onClick={() => setShowDeleteConfirm(true)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Job Details Card */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            {/* Removed Grid container - using direct Box layout */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: { xs: 2, md: 1.5 }
            }}>
              {/* Left column - Job details */}
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    {/* Title Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {job.jobNumber || job.id} - {job.client}
                      </Typography>
                      <Chip
                        label={job.status || 'Planning'}
                        variant="outlined"
                        size="small"
                        sx={{
                          fontWeight: 500,
                          borderColor: 'text.secondary',
                          color: 'text.secondary'
                        }}
                      />
                    </Box>

                    {/* Details Row */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                      {job.location && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                          Location: {job.location}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {(job.startDate || job.endDate) && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            {new Date(job.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {job.endDate && (
                              <> - {new Date(job.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                            )}
                          </Typography>
                        )}
                        {job.sites && job.sites.length > 0 && (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                              •
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                              {job.sites.length} {job.sites.length === 1 ? 'Site' : 'Sites'}
                            </Typography>
                          </>
                        )}
                        {job.priority && job.priority !== 'Normal' && (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                              •
                            </Typography>
                            <Chip
                              label={job.priority}
                              size="small"
                              color={
                                job.priority === 'Urgent' ? 'error' :
                                job.priority === 'High' ? 'warning' :
                                'default'
                              }
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Progress and Stack Row */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                        Equipment Progress: <strong>{jobStats.receivedItems} received / {jobStats.totalItems} planned</strong>
                      </Typography>


                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                          Stack: {(() => {
                            // Check for new stackLocations format first
                            if (job.stackLocations && job.stackLocations.length > 0) {
                              if (!racks || racks.length === 0 || !bays || bays.length === 0) {
                                return 'Loading...';
                              }

                              const stackLabels = job.stackLocations.map(location => {
                                const warehouse = warehouses.find(w => w.id === location.warehouseId);
                                const rack = racks.find(r => r.id === location.rackId);
                                const bay = bays.find(b => b.id === location.bayId);
                                return `${warehouse?.name || '?'}-${rack?.name || '?'} ${bay?.name || '?'}`;
                              });

                              return stackLabels.join(', ');
                            }

                            // Fallback to old format for backward compatibility
                            if (!job.stackWarehouseId) return 'Not assigned';
                            if (!warehouses || warehouses.length === 0) {
                              return 'Loading...';
                            }

                            const warehouse = warehouses.find(w => w.id === job.stackWarehouseId);
                            const rack = racks.find(r => r.id === job.stackRackId);
                            const bay = bays.find(b => b.id === job.stackBayId);

                            const parts = [];
                            if (warehouse?.name) parts.push(warehouse.name);
                            if (rack?.name) parts.push(rack.name);
                            const location = parts.join('-');

                            if (bay?.name) {
                              return `${location} ${bay.name}`;
                            } else {
                              return location || 'Unknown';
                            }
                          })()}
                        </Typography>

                        {/* Multi-Stack Location Controls */}
                        <Box sx={{ display: 'flex', gap: 1, ml: 1, alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingStackLocations(true);
                              // Initialize temp locations with current data
                              let currentLocations = [];

                              if (job?.stackLocations && job.stackLocations.length > 0) {
                                // Use new stackLocations format
                                currentLocations = [...job.stackLocations];
                              } else if (job?.stackRackId && job?.stackBayId) {
                                // Convert old format to new format
                                currentLocations = [{ rackId: job.stackRackId, bayId: job.stackBayId }];
                              } else {
                                // Start with one empty location
                                currentLocations = [{ rackId: '', bayId: '' }];
                              }

                              setTempStackLocations(currentLocations);
                            }}
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            Edit Stacks
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Notes Section - Right Side */}
                  <Box sx={{
                    flex: { xs: '1 1 auto', md: '0 0 auto' },
                    width: { xs: '100%', md: '350px' },
                    minWidth: 0
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Notes
                      </Typography>
                      <IconButton size="small" onClick={() => {
                        setEditingNotes(true);
                        setNotesText(job.notes || '');
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{
                      p: 2,
                      bgcolor: '#fafafa',
                      borderRadius: 1,
                      minHeight: 120,
                      border: '1px solid #e0e0e0'
                    }}>
                      <LinkifiedText text={job.notes || ''} />
                    </Box>
                  </Box>
            </Box>
          </Paper>
        </>
      )}

      {/* Main Content - PM Demo or Regular Job View */}
      <Box>
        {/* Project Management Demo - Replaces normal view for PM demo job */}
        {isPMDemo ? (
          <JobProjectView
            job={job}
            jobEquipment={jobEquipment}
            equipmentPlans={equipmentPlans}
          />
        ) : (
          <>
            {/* Site Management Component */}
            <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
              <JobSiteManager
                jobId={jobId}
                sites={job.sites || []}
                equipmentPlans={equipmentPlans}
                warehouses={warehouses}
                racks={racks}
                bays={bays}
                onSitesUpdate={(updatedSites) => {
                  setJob(prev => ({ ...prev, sites: updatedSites }));
                }}
              />
            </Paper>

            {/* Equipment Planning Section */}
            <JobEquipmentPlanning jobId={jobId} />
          </>
        )}
      </Box>
      
      {/* Equipment Dialog */}
      <Dialog 
        open={showEquipmentDialog} 
        onClose={() => setShowEquipmentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formMode === 'add' ? 'Add Equipment' : 'Edit Equipment'}
        </DialogTitle>
        
        {/* Display error as an alert if present */}
        {error && (
          <Alert severity="error" sx={{ mx: 3 }}>
            {error}
          </Alert>
        )}
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Barcode Scanner for equipment identification */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Scan Barcode to Auto-Fill Equipment Details
                </Typography>
                <BarcodeScanner
                  onScanSuccess={(code) => {
                    // TODO: In a real implementation, this would lookup the equipment
                    // details from your inventory system based on the barcode
                    console.log('Scanned barcode:', code);
                    setError('Barcode scanned: ' + code + '. In a production environment, this would auto-fill all equipment details.');
                  }}
                  height="200px"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Site</InputLabel>
                <Select
                  name="siteId"
                  value={equipmentFormData.siteId}
                  onChange={handleEquipmentFormChange}
                  label="Site *"
                >
                  {job.sites && job.sites.map(site => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description *"
                fullWidth
                value={equipmentFormData.description}
                onChange={handleEquipmentFormChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="manufacturer"
                label="Manufacturer"
                fullWidth
                value={equipmentFormData.manufacturer}
                onChange={handleEquipmentFormChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="model"
                label="Model"
                fullWidth
                value={equipmentFormData.model}
                onChange={handleEquipmentFormChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="serialNumber"
                label="Serial Number"
                fullWidth
                value={equipmentFormData.serialNumber}
                onChange={handleEquipmentFormChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="partNumber"
                label="Part Number"
                fullWidth
                value={equipmentFormData.partNumber}
                onChange={handleEquipmentFormChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                name="quantity"
                label="Quantity *"
                type="number"
                fullWidth
                value={equipmentFormData.quantity}
                onChange={handleEquipmentFormChange}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <FormControlLabel 
                control={
                  <Switch 
                    checked={equipmentFormData.isReceived}
                    onChange={handleEquipmentFormChange}
                    name="isReceived"
                    color="success"
                  />
                }
                label="Mark as Received"
              />
            </Grid>
            
            {equipmentFormData.isReceived && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="receivedDate"
                    label="Received Date"
                    type="date"
                    fullWidth
                    value={equipmentFormData.receivedDate}
                    onChange={handleEquipmentFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="warehouseLocation"
                    label="Warehouse Location"
                    fullWidth
                    value={equipmentFormData.warehouseLocation}
                    onChange={handleEquipmentFormChange}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={equipmentFormData.notes}
                onChange={handleEquipmentFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEquipmentDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveEquipment} variant="contained" color="primary">
            {formMode === 'add' ? 'Add Equipment' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeDeleteConfirmation}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {equipmentToDelete?.description || 'this equipment'}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleDeleteEquipment(equipmentToDelete?.id)} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Bulk Update Dialog */}
      <Dialog
        open={showBulkUpdateDialog}
        onClose={() => setShowBulkUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bulk Update Equipment Status
        </DialogTitle>
        
        {/* Display error as an alert if present */}
        {error && (
          <Alert severity="error" sx={{ mx: 3 }}>
            {error}
          </Alert>
        )}
        
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedSiteId 
              ? `Update all pending equipment for site: ${job.sites.find(s => s.id === selectedSiteId)?.name || ''}`
              : 'Update all pending equipment across all sites'}
          </DialogContentText>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkUpdateStatus.isReceived}
                    onChange={handleBulkStatusChange}
                    name="isReceived"
                    color="success"
                  />
                }
                label="Mark as Received"
              />
            </Grid>
            
            {bulkUpdateStatus.isReceived && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="receivedDate"
                    label="Received Date"
                    type="date"
                    fullWidth
                    value={bulkUpdateStatus.receivedDate}
                    onChange={handleBulkStatusChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="warehouseLocation"
                    label="Warehouse Location"
                    fullWidth
                    value={bulkUpdateStatus.warehouseLocation}
                    onChange={handleBulkStatusChange}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                This will update {selectedSiteId 
                  ? jobEquipment.filter(item => item.siteId === selectedSiteId && !item.isReceived).length 
                  : jobEquipment.filter(item => !item.isReceived).length} pending equipment items.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowBulkUpdateDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyBulkUpdate}
            variant="contained"
            color="success"
            startIcon={<ReceivedIcon />}
          >
            Update All Items
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Site Management Dialog removed - now using JobSiteManager component */}
      
      {/* Multi-Stack Location Editor Dialog */}
      <Dialog
        open={editingStackLocations}
        onClose={() => setEditingStackLocations(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Stack Locations</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add multiple rack and bay combinations for this job's stack. Examples: 3A, 5B, 6D
            </Typography>
            
            {tempStackLocations.map((location, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Rack</InputLabel>
                  <Select
                    value={location.rackId || ''}
                    onChange={(e) => {
                      const newLocations = [...tempStackLocations];
                      newLocations[index].rackId = e.target.value;
                      setTempStackLocations(newLocations);
                    }}
                    label="Rack"
                  >
                    <MenuItem value="">Select Rack</MenuItem>
                    {racks.map(rack => (
                      <MenuItem key={rack.id} value={rack.id}>
                        {rack.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Bay</InputLabel>
                  <Select
                    value={location.bayId || ''}
                    onChange={(e) => {
                      const newLocations = [...tempStackLocations];
                      newLocations[index].bayId = e.target.value;
                      setTempStackLocations(newLocations);
                    }}
                    label="Bay"
                  >
                    <MenuItem value="">Select Bay</MenuItem>
                    {bays.map(bay => (
                      <MenuItem key={bay.id} value={bay.id}>
                        {bay.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <IconButton
                  onClick={() => {
                    const newLocations = tempStackLocations.filter((_, i) => i !== index);
                    setTempStackLocations(newLocations);
                  }}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setTempStackLocations([...tempStackLocations, { rackId: '', bayId: '' }]);
              }}
              variant="outlined"
              size="small"
            >
              Add Stack Location
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingStackLocations(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                // Filter out incomplete locations
                const validLocations = tempStackLocations.filter(loc => loc.rackId && loc.bayId);
                
                // Update job with new stack locations
                // For now, store as JSON string in a notes field or custom field
                const updateData = { 
                  stackLocations: validLocations,
                  // Also store as a formatted string for backward compatibility
                  stackLocationText: validLocations.map(loc => {
                    const rack = racks.find(r => r.id === loc.rackId);
                    const bay = bays.find(b => b.id === loc.bayId);
                    return `${rack?.name}${bay?.name}`;
                  }).join(', ')
                };
                
                console.log('Updating job with stack locations:', updateData);
                const response = await jobApi.updateJob(jobId, updateData);
                console.log('Job update response:', response.data);
                setJob(response.data);
                
                // Show success message
                const locationStrings = validLocations.map(loc => {
                  const rack = racks.find(r => r.id === loc.rackId);
                  const bay = bays.find(b => b.id === loc.bayId);
                  return `${rack?.name}${bay?.name}`;
                });
                enqueueSnackbar(`Stack locations updated: ${locationStrings.join(', ')}`, { variant: 'success' });
                
                setEditingStackLocations(false);
              } catch (error) {
                console.error('Error updating stack locations:', error);
                enqueueSnackbar('Failed to update stack locations', { variant: 'error' });
              }
            }}
            variant="contained"
            disabled={tempStackLocations.filter(loc => loc.rackId && loc.bayId).length === 0}
          >
            Save Stack Locations
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status History Dialog */}
      <StatusHistoryDialog
        open={historyDialogOpen}
        onClose={handleHistoryClose}
        equipment={historyItem}
        onEquipmentUpdate={(updatedEquipment) => {
          // Update the equipment in the equipment plans list
          setEquipmentPlans(prev =>
            prev.map(item =>
              item.id === updatedEquipment.id ? updatedEquipment : item
            )
          );
        }}
      />

      {/* Edit Stack Locations Dialog */}
      <Dialog
        open={editingStackLocations}
        onClose={() => setEditingStackLocations(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Stack Locations</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {tempStackLocations?.map((location, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Warehouse</InputLabel>
                  <Select
                    value={location.warehouseId || ''}
                    label="Warehouse"
                    onChange={(e) => {
                      const updated = [...tempStackLocations];
                      updated[index] = { ...updated[index], warehouseId: e.target.value };
                      setTempStackLocations(updated);
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {warehouses.map(w => (
                      <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Rack</InputLabel>
                  <Select
                    value={location.rackId || ''}
                    label="Rack"
                    onChange={(e) => {
                      const updated = [...tempStackLocations];
                      updated[index] = { ...updated[index], rackId: e.target.value };
                      setTempStackLocations(updated);
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {racks.map(r => (
                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Bay</InputLabel>
                  <Select
                    value={location.bayId || ''}
                    label="Bay"
                    onChange={(e) => {
                      const updated = [...tempStackLocations];
                      updated[index] = { ...updated[index], bayId: e.target.value };
                      setTempStackLocations(updated);
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {bays.map(b => (
                      <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => {
                    setTempStackLocations(tempStackLocations.filter((_, i) => i !== index));
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setTempStackLocations([...tempStackLocations, { warehouseId: '', rackId: '', bayId: '' }]);
              }}
            >
              Add Location
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingStackLocations(false)}>Cancel</Button>
          <Button onClick={handleStackLocationSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Notes Dialog */}
      <Dialog
        open={editingNotes}
        onClose={() => setEditingNotes(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Notes</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder="Enter notes..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingNotes(false)}>Cancel</Button>
          <Button onClick={handleNotesUpdate} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Actions Modal */}
      <QuickActionsModal
        open={showQuickActions}
        onClose={() => setShowQuickActions(false)}
        currentJob={job}
      />

      {/* Job Location QR Poster */}
      <JobLocationQRPoster
        job={job}
        open={showJobQRPoster}
        onClose={() => setShowJobQRPoster(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this job? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteJob} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}